/**
 * GenerateDocsView — Generate Documentation Screen
 *
 * 5-step guided flow:
 *   1. Select Scope       — scope selector; file picker when 'selected' mode
 *   2. Doc Package        — choose documentation artifact types
 *   3. Configure Output   — detail level, dependencies, AI enhancement, format
 *   4. Review Plan        — full summary + launch generation
 *   5. Results            — artifacts, file-level status table, export actions
 *
 * Steps 1–4 layout and step 5 structure are UI-only in this phase.
 * The generation itself (step 4 → 5) uses the existing backend wiring.
 *
 * API endpoints (unchanged):
 *   POST /documentation/generate        — start generation job
 *   GET  /documentation/{id}/status     — poll progress
 *   GET  /documentation/{id}/artifacts  — list completed artifacts
 *   GET  /documentation/{id}/artifact/{type}/{file} — download / preview
 */

import { useState, useEffect, useRef, useCallback } from 'react'
import {
  ArrowLeft, ChevronRight, ChevronDown, Settings, HelpCircle, User,
  Home, Workflow, Plug, FileBarChart, BarChart2, LayoutGrid,
  FileText, BookOpen, Database, Code2, Network,
  CheckCircle2, Circle, Lock, Play, Download, Eye,
  Loader2, Layers, Sparkles, FolderOpen, ToggleLeft, ToggleRight,
  AlertCircle, X, Search, MessageSquare, RefreshCw, Archive,
} from 'lucide-react'
import { getAPIBaseUrl, isQA } from '../../environment'
import './GenerateDocsView.css'
import '../runs/RunsView.css'

// ─── Documentation type definitions ──────────────────────────────────────────

const DOC_TYPES = [
  {
    id: 'technical-spec',
    icon: FileText,
    label: 'Technical Specification',
    desc: 'Full program-level specification with data structures, logic flow, and processing rules.',
    bestFor: 'Developers, architects, and onboarding',
    badge: 'Recommended',
    badgeColor: 'blue',
    requiresStep: null,
    prerequisiteLabel: null,
  },
  {
    id: 'data-dictionary',
    icon: Database,
    label: 'Data Dictionary',
    desc: 'Comprehensive listing of all data items, copybooks, types, and usage patterns.',
    bestFor: 'Data analysts and DBAs',
    badge: null,
    requiresStep: null,
    prerequisiteLabel: null,
  },
  {
    id: 'dependency-map',
    icon: Layers,
    label: 'Dependency Report',
    desc: 'Visual and tabular dependency graph exported as a structured document.',
    bestFor: 'Impact analysis and migration planning',
    badge: null,
    requiresStep: null,
    prerequisiteLabel: null,
  },
  {
    id: 'business-rules',
    icon: BookOpen,
    label: 'Business Rules Catalog',
    desc: 'Extracted business logic rules mapped to source paragraphs and decision points.',
    bestFor: 'Business analysts and QA teams',
    badge: null,
    requiresStep: 'monolith-identifier',
    prerequisiteLabel: 'Business Logic',
  },
  {
    id: 'code-annotations',
    icon: Code2,
    label: 'Inline Code Annotations',
    desc: 'AI-generated inline comments and section summaries embedded in source files.',
    bestFor: 'Code review and maintainability',
    badge: null,
    requiresStep: 'code-refactor',
    prerequisiteLabel: 'Refactor Code',
  },
  {
    id: 'api-contracts',
    icon: Network,
    label: 'API Contracts',
    desc: 'Generated interface contracts for service boundaries and inter-program calls.',
    bestFor: 'Integration teams and service design',
    badge: 'New',
    badgeColor: 'green',
    requiresStep: 'service-gen',
    prerequisiteLabel: 'Service Generation',
  },
]

// ─── Step definitions ─────────────────────────────────────────────────────────

const STEPS = [
  { id: 1, label: 'Select Scope',          desc: 'Choose files and scope'           },
  { id: 2, label: 'Documentation Package', desc: 'Select documentation artifacts'  },
  { id: 3, label: 'Configure Output',      desc: 'Format, detail and AI settings'  },
  { id: 4, label: 'Review Plan',           desc: 'Review and launch generation'    },
  { id: 5, label: 'Results',               desc: 'Review and export artifacts'     },
]

// ─── Scope options ────────────────────────────────────────────────────────────

const SCOPE_OPTIONS = [
  { id: 'all',      label: 'Entire Application', desc: 'All analyzed programs and copybooks' },
  { id: 'programs', label: 'Programs Only',       desc: 'COBOL programs, excluding copybooks' },
  { id: 'selected', label: 'Selected Files',      desc: 'Manually choose specific files'      },
]

// ─── Artifact slug map (for Review Plan display) ─────────────────────────────

const DOC_TYPE_SLUGS = {
  'technical-spec':   'technical_spec',
  'data-dictionary':  'data_dictionary',
  'dependency-map':   'dependency_report',
  'business-rules':   'business_rules_catalog',
  'code-annotations': 'code_annotations',
  'api-contracts':    'api_contracts',
}

// ─── Component ────────────────────────────────────────────────────────────────

function GenerateDocsView({
  project,
  caJobId = null,
  accountId = null,
  onBack,
  onBackToProjects,
  onGoToAnalyze,
  onGoToExtractBizLogic,
  onGoToDecomposeCode,
  onGoToServiceGeneration,
  onGoToJavaPkg,
  onGoToRefineCode,
  onGoToRefactorCode,
  onOpenSettings,
  onOpenHelp,
  onOpenReports,
  userProfile = {},
  availableSteps = new Set(),
}) {
  // ── Core navigation state
  const [activeStep,       setActiveStep]       = useState(1)
  const [selectedScope,    setSelectedScope]    = useState('all')
  const [selectedDocTypes, setSelectedDocTypes] = useState(new Set(['technical-spec', 'data-dictionary']))
  const [showUserMenu,     setShowUserMenu]     = useState(false)
  const [showMoreMenu,     setShowMoreMenu]     = useState(false)
  const [outputFormat,     setOutputFormat]     = useState('markdown')
  const [includeAI,        setIncludeAI]        = useState(true)

  // ── New step 1–3 controls
  const [detailLevel,  setDetailLevel]  = useState('detailed')  // 'summary' | 'detailed'
  const [includeDeps,  setIncludeDeps]  = useState(true)
  const [fileSearch,   setFileSearch]   = useState('')

  // ── Generation state
  const [docJobId,       setDocJobId]       = useState(null)
  const [genStatus,      setGenStatus]      = useState('idle')   // 'idle'|'running'|'completed'|'failed'
  const [genProgress,    setGenProgress]    = useState(0)
  const [genMessage,     setGenMessage]     = useState('')
  const [genError,       setGenError]       = useState(null)
  const [artifacts,      setArtifacts]      = useState([])

  // ── Preview modal state
  const [previewOpen,    setPreviewOpen]    = useState(false)
  const [previewText,    setPreviewText]    = useState('')
  const [previewLabel,   setPreviewLabel]   = useState('')
  const [previewLoading, setPreviewLoading] = useState(false)
  const [previewFormat,  setPreviewFormat]  = useState('text')

  const closePreview = useCallback(() => {
    if (previewFormat === 'pdf' && previewText.startsWith('blob:')) {
      URL.revokeObjectURL(previewText)
    }
    setPreviewOpen(false)
  }, [previewFormat, previewText])

  // ── Expandable error details
  const [expandedErrors, setExpandedErrors] = useState(new Set())

  const pollRef = useRef(null)

  // ── Outcome classification ────────────────────────────────────────────────

  const genSucceededCount = artifacts.filter(a => a.format !== 'error').length
  const genFailedCount    = artifacts.filter(a => a.format === 'error').length
  const outcomeStatus = (
    genStatus !== 'completed' ? genStatus
    : genFailedCount === 0    ? 'completed'
    : genSucceededCount === 0 ? 'all-failed'
    :                           'partial'
  )

  const projectName        = project?.name || 'Project'
  const effectiveAccountId = accountId || project?.account || ''

  const getShortName = () => {
    if (userProfile.firstName && userProfile.lastName) {
      return `${userProfile.firstName} ${userProfile.lastName.charAt(0)}.`
    }
    return userProfile.displayName || 'User'
  }

  // ── Gating helpers ────────────────────────────────────────────────────────

  const isAvailable = (dt) => {
    if (!dt.requiresStep) return true
    return availableSteps.has(dt.requiresStep)
  }

  const gateTooltip = (dt) =>
    `To enable this artifact, run the ${dt.prerequisiteLabel} step first.`

  const toggleDocType = (id) => {
    const dt = DOC_TYPES.find(d => d.id === id)
    if (!dt || !isAvailable(dt)) return
    setSelectedDocTypes(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  // ── Navigation control ────────────────────────────────────────────────────

  const canAdvance = () => {
    if (activeStep === 1) return true
    if (activeStep === 2) return selectedDocTypes.size > 0
    if (activeStep === 3) return true
    return false
  }

  // Footer Back/Continue shown only for steps 1–3
  // Step 4 owns its Generate button; step 5 owns its export actions
  const showFooter = activeStep >= 1 && activeStep <= 3

  // ── API helpers ───────────────────────────────────────────────────────────

  const getHeaders = (json = false) => {
    const token = localStorage.getItem('modernizeit_token') || ''
    return {
      ...(json ? { 'Content-Type': 'application/json' } : {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    }
  }

  // ── startGeneration ───────────────────────────────────────────────────────

  const startGeneration = useCallback(async () => {
    if (!caJobId) {
      setGenError('No Code Analysis job is linked to this session. Please run the Analyze step first.')
      setGenStatus('failed')
      return
    }

    const API_BASE = getAPIBaseUrl()

    const storedUser = (() => {
      try { return JSON.parse(localStorage.getItem('modernizeit_user') || '{}') } catch { return {} }
    })()
    const userId = storedUser?.user_id || storedUser?.id || ''

    setGenStatus('running')
    setGenProgress(0)
    setGenMessage('Starting…')
    setGenError(null)
    setArtifacts([])

    try {
      const resp = await fetch(`${API_BASE}/documentation/generate`, {
        method:  'POST',
        headers: getHeaders(true),
        body:    JSON.stringify({
          ca_job_id:     caJobId,
          account_id:    effectiveAccountId,
          user_id:       userId,
          app_name:      projectName,
          doc_types:     [...selectedDocTypes],
          include_ai:    includeAI,
          output_format: outputFormat,
        }),
      })

      if (!resp.ok) {
        const err = await resp.json().catch(() => ({ detail: `HTTP ${resp.status}` }))
        throw new Error(err.detail || `HTTP ${resp.status}`)
      }

      const data = await resp.json()
      setDocJobId(data.job_id)

    } catch (e) {
      setGenStatus('failed')
      setGenError(e.message)
    }
  }, [caJobId, effectiveAccountId, projectName, selectedDocTypes, includeAI, outputFormat])

  // ── Polling effect ────────────────────────────────────────────────────────

  useEffect(() => {
    if (!docJobId || genStatus !== 'running') return

    const API_BASE = getAPIBaseUrl()

    const poll = async () => {
      try {
        const resp = await fetch(`${API_BASE}/documentation/${docJobId}/status`, {
          headers: getHeaders(),
        })
        if (!resp.ok) return

        const data = await resp.json()
        setGenProgress(data.progress_pct ?? 0)
        setGenMessage(data.progress_msg ?? '')

        if (data.status === 'completed') {
          clearInterval(pollRef.current)
          setGenProgress(100)
          setGenMessage('Documentation generated successfully')
          setGenStatus('completed')

          try {
            const artResp = await fetch(`${API_BASE}/documentation/${docJobId}/artifacts`, {
              headers: getHeaders(),
            })
            if (artResp.ok) {
              const artData = await artResp.json()
              setArtifacts(artData.artifacts || [])
            }
          } catch (_) { /* non-fatal */ }

          // Auto-advance to Results step
          setActiveStep(5)

        } else if (data.status === 'failed') {
          clearInterval(pollRef.current)
          setGenStatus('failed')
          setGenError(data.error || 'Documentation generation failed')
        }
      } catch (_) {
        // Non-fatal — keep polling
      }
    }

    poll()
    pollRef.current = setInterval(poll, 2000)
    return () => clearInterval(pollRef.current)
  }, [docJobId, genStatus])

  // ── downloadArtifact ──────────────────────────────────────────────────────

  const downloadArtifact = useCallback(async (downloadUrl, filename, format) => {
    const API_BASE = getAPIBaseUrl()
    try {
      const resp = await fetch(`${API_BASE}${downloadUrl}`, { headers: getHeaders() })
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`)

      let blob
      if (format === 'pdf') {
        const buf = await resp.arrayBuffer()
        blob = new Blob([buf], { type: 'application/pdf' })
      } else if (format === 'html') {
        const text = await resp.text()
        blob = new Blob([text], { type: 'text/html; charset=utf-8' })
      } else {
        const text = await resp.text()
        blob = new Blob([text], { type: 'text/markdown; charset=utf-8' })
      }

      const url = URL.createObjectURL(blob)
      const a   = document.createElement('a')
      a.href     = url
      a.download = filename
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    } catch (e) {
      console.error('[GenDocs] Download failed:', e)
    }
  }, [])

  // ── openPreview ───────────────────────────────────────────────────────────

  const openPreview = useCallback(async (downloadUrl, label, format) => {
    const API_BASE = getAPIBaseUrl()
    setPreviewLabel(label)
    setPreviewText('')
    setPreviewFormat('text')
    setPreviewLoading(true)
    setPreviewOpen(true)
    try {
      const resp = await fetch(`${API_BASE}${downloadUrl}`, { headers: getHeaders() })
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`)

      if (format === 'pdf') {
        const buf  = await resp.arrayBuffer()
        const blob = new Blob([buf], { type: 'application/pdf' })
        setPreviewText(URL.createObjectURL(blob))
        setPreviewFormat('pdf')
      } else if (format === 'html') {
        setPreviewText(await resp.text())
        setPreviewFormat('html')
      } else {
        setPreviewText(await resp.text())
        setPreviewFormat('text')
      }
    } catch (e) {
      setPreviewText(`Error loading preview: ${e.message}`)
      setPreviewFormat('text')
    } finally {
      setPreviewLoading(false)
    }
  }, [])

  // ── getFriendlyErrorMessage ───────────────────────────────────────────────

  const getFriendlyErrorMessage = (art) => {
    const raw = (art.error || '').toLowerCase()
    if (
      raw.includes('internally inconsistent') ||
      raw.includes('irreconcilable')          ||
      raw.includes('cross_reference')         ||
      raw.includes('file inventory')          ||
      raw.includes('consistency error')       ||
      raw.includes('techspecconsistencyerror')
    ) {
      return 'The analysis data for this document is incomplete. Re-running Code Analysis should resolve this.'
    }
    if (raw.includes('credentials') || raw.includes('aws') || raw.includes('s3')) {
      return 'This document could not be generated due to a storage or credentials issue. Check your AWS configuration in Settings.'
    }
    if (
      raw.includes('parse_results')  ||
      raw.includes('failed to load') ||
      raw.includes('artifact not found')
    ) {
      return 'The source analysis data could not be retrieved. Re-running Code Analysis should refresh the artifacts.'
    }
    return 'This document could not be generated. Please try again, or re-run Code Analysis if the issue persists.'
  }

  // ── resetGeneration helper ────────────────────────────────────────────────

  const resetGeneration = () => {
    setActiveStep(1)
    setGenStatus('idle')
    setDocJobId(null)
    setArtifacts([])
    setGenProgress(0)
    setGenMessage('')
    setGenError(null)
    setExpandedErrors(new Set())
  }

  // ── Render step sidebar item ──────────────────────────────────────────────

  const renderStepItem = (step) => {
    const isDone   = step.id < activeStep
    const isActive = step.id === activeStep
    const isLocked = step.id > activeStep

    return (
      <button
        key={step.id}
        className={`gd-step-btn ${isActive ? 'active' : ''} ${isDone ? 'done' : ''} ${isLocked ? 'locked' : ''}`}
        onClick={() => !isLocked && setActiveStep(step.id)}
        disabled={isLocked}
      >
        <div className="gd-step-icon">
          {isDone
            ? <CheckCircle2 size={16} />
            : isLocked
              ? <Lock size={14} />
              : <Circle size={16} />}
        </div>
        <div className="gd-step-text">
          <span className="gd-step-label">{step.label}</span>
          <span className="gd-step-desc">{step.desc}</span>
        </div>
      </button>
    )
  }

  // ── STEP 1: Select Scope ──────────────────────────────────────────────────

  const SCOPE_ICONS = { all: Layers, programs: FileText, selected: Search }

  const renderStep1 = () => (
    <div className="gd-step-content">
      <div className="gd-step-header">
        <h2 className="gd-step-title">Select Scope</h2>
        <p className="gd-step-subtitle">
          Choose what source material to document. Documentation will be generated
          from the most recent Code Analysis run for <strong>{projectName}</strong>.
        </p>
        <div className="gd-source-chip">
          <FolderOpen size={13} />
          <span>{projectName}</span>
          <span className="gd-source-chip-sep">·</span>
          <span className={`gd-source-chip-status ${caJobId ? 'ready' : 'warn'}`}>
            {caJobId ? `CA ${caJobId.slice(0, 8)}… · Ready` : 'No CA job linked'}
          </span>
        </div>
      </div>

      {!caJobId && (
        <div className="gd-warn-banner">
          <AlertCircle size={15} />
          <span>
            No Code Analysis job is linked. Generation will fail unless you{' '}
            <button className="gd-warn-link" onClick={onGoToAnalyze}>run Analyze first</button>.
          </span>
        </div>
      )}

      {/* Scope selector */}
      <div className="gd-scope-cards">
        {SCOPE_OPTIONS.map(opt => {
          const ScopeIcon = SCOPE_ICONS[opt.id]
          return (
            <button
              key={opt.id}
              className={`gd-scope-card ${selectedScope === opt.id ? 'selected' : ''}`}
              onClick={() => setSelectedScope(opt.id)}
            >
              <div className="gd-scope-card-iconbox">
                <ScopeIcon size={20} />
              </div>
              <div className="gd-scope-card-text">
                <span className="gd-scope-card-name">{opt.label}</span>
                <span className="gd-scope-card-desc">{opt.desc}</span>
              </div>
              <div className="gd-scope-card-check">
                {selectedScope === opt.id
                  ? <CheckCircle2 size={17} />
                  : <Circle size={17} />}
              </div>
            </button>
          )
        })}
      </div>

      {/* File picker — only when 'selected' scope */}
      {selectedScope === 'selected' && (
        <div className="gd-section">
          <h3 className="gd-section-label">Select Files</h3>
          <div className="gd-file-picker">
            <div className="gd-file-search-row">
              <Search size={14} className="gd-file-search-icon" />
              <input
                type="text"
                className="gd-file-search-input"
                placeholder="Search by name or path…"
                value={fileSearch}
                onChange={e => setFileSearch(e.target.value)}
              />
            </div>
            <div className="gd-file-table-wrap">
              <table className="gd-file-table">
                <thead>
                  <tr>
                    <th className="gd-file-col-check">
                      <input type="checkbox" disabled title="Select all" />
                    </th>
                    <th className="gd-file-col-name">Name</th>
                    <th className="gd-file-col-type">Type</th>
                    <th className="gd-file-col-lines">Lines</th>
                    <th className="gd-file-col-path">Path</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td colSpan={5} className="gd-file-table-empty">
                      File list will load from Code Analysis results
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div className="gd-file-table-footer">
              <span className="gd-file-table-count">0 files selected</span>
            </div>
          </div>
        </div>
      )}
    </div>
  )

  // ── STEP 2: Documentation Package ────────────────────────────────────────

  const renderStep2 = () => {
    const availableTypes = DOC_TYPES.filter(dt => isAvailable(dt))
    const gatedTypes     = DOC_TYPES.filter(dt => !isAvailable(dt))

    const renderDocCard = (dt) => {
      const Icon     = dt.icon
      const available = isAvailable(dt)
      const selected  = available && selectedDocTypes.has(dt.id)
      return (
        <button
          key={dt.id}
          className={`gd-doctype-card ${selected ? 'selected' : ''} ${!available ? 'gated' : ''}`}
          onClick={() => toggleDocType(dt.id)}
          disabled={!available}
          title={!available ? gateTooltip(dt) : undefined}
        >
          <div className="gd-doctype-card-top">
            <div className="gd-doctype-iconbox">
              {!available ? <Lock size={15} /> : <Icon size={17} />}
            </div>
            <div className="gd-doctype-card-badges">
              {dt.badge && available && (
                <span className={`gd-doctype-badge ${dt.badgeColor || 'blue'}`}>{dt.badge}</span>
              )}
              {!available && (
                <span className="gd-doctype-gate-label">Step required</span>
              )}
            </div>
            <div className={`gd-doctype-check ${selected ? 'on' : ''}`}>
              {selected && <CheckCircle2 size={15} />}
            </div>
          </div>
          <span className="gd-doctype-label">{dt.label}</span>
          <span className="gd-doctype-desc">{dt.desc}</span>
          {dt.bestFor && available && (
            <span className="gd-doctype-best-for">Best for: {dt.bestFor}</span>
          )}
          {!available && (
            <span className="gd-doctype-prereq">Requires: {dt.prerequisiteLabel}</span>
          )}
        </button>
      )
    }

    return (
      <div className="gd-step-content">
        <div className="gd-step-header">
          <h2 className="gd-step-title">Documentation Package</h2>
          <p className="gd-step-subtitle">
            Choose which documentation artifacts to include. Each type produces a
            separate structured document from your analyzed source.
          </p>
          {selectedDocTypes.size > 0 && (
            <div className="gd-selection-chip">
              <CheckCircle2 size={13} />
              <span>{selectedDocTypes.size} artifact{selectedDocTypes.size !== 1 ? 's' : ''} selected</span>
            </div>
          )}
        </div>

        {/* Available artifacts */}
        <div className="gd-doctype-grid">
          {availableTypes.map(renderDocCard)}
        </div>

        {/* Gated artifacts — only show if any exist */}
        {gatedTypes.length > 0 && (
          <div className="gd-section">
            <h3 className="gd-section-label">Requires additional pipeline steps</h3>
            <div className="gd-doctype-grid gd-doctype-grid-gated">
              {gatedTypes.map(renderDocCard)}
            </div>
          </div>
        )}
      </div>
    )
  }

  // ── STEP 3: Configure Output ──────────────────────────────────────────────

  const renderStep3 = () => (
    <div className="gd-step-content">
      <div className="gd-step-header">
        <h2 className="gd-step-title">Configure Output</h2>
        <p className="gd-step-subtitle">
          Set the depth, format, and enrichment level for your documentation package.
        </p>
      </div>

      {/* Group 1: Content settings — Detail level + Format */}
      <div className="gd-settings-group">
        <div className="gd-settings-group-header">Content Settings</div>

        <div className="gd-settings-row">
          <div className="gd-settings-row-label">
            <span className="gd-settings-label">Detail Level</span>
            <span className="gd-settings-hint">How much depth each document includes</span>
          </div>
          <div className="gd-option-row">
            {[
              { id: 'summary',  label: 'Summary',  desc: 'High-level overview per program' },
              { id: 'detailed', label: 'Detailed', desc: 'Full specification with data flows and logic' },
            ].map(opt => (
              <button
                key={opt.id}
                className={`gd-option-card ${detailLevel === opt.id ? 'selected' : ''}`}
                onClick={() => setDetailLevel(opt.id)}
              >
                <div className="gd-option-radio">
                  {detailLevel === opt.id
                    ? <CheckCircle2 size={15} />
                    : <Circle size={15} />}
                </div>
                <div className="gd-option-text">
                  <span className="gd-option-name">{opt.label}</span>
                  <span className="gd-option-desc">{opt.desc}</span>
                </div>
              </button>
            ))}
          </div>
        </div>

        <div className="gd-settings-row gd-settings-row-border">
          <div className="gd-settings-row-label">
            <span className="gd-settings-label">Output Format</span>
            <span className="gd-settings-hint">File format for all generated artifacts</span>
          </div>
          <div className="gd-format-row">
            {['markdown', 'html', 'pdf'].map(fmt => (
              <button
                key={fmt}
                className={`gd-format-btn ${outputFormat === fmt ? 'selected' : ''}`}
                onClick={() => setOutputFormat(fmt)}
              >
                {fmt.toUpperCase()}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Group 2: Enrichment options — Dependencies + AI */}
      <div className="gd-settings-group">
        <div className="gd-settings-group-header">Enrichment Options</div>

        <div className="gd-toggle-row gd-toggle-row-ingroup">
          <div className="gd-toggle-text">
            <span className="gd-toggle-label">Include Dependencies</span>
            <span className="gd-toggle-desc">
              Add program call trees, copybook references, and file dependencies to each artifact.
            </span>
          </div>
          <button
            className={`gd-toggle-btn ${includeDeps ? 'on' : ''}`}
            onClick={() => setIncludeDeps(v => !v)}
            title={includeDeps ? 'Exclude dependencies' : 'Include dependencies'}
          >
            {includeDeps ? <ToggleRight size={28} /> : <ToggleLeft size={28} />}
          </button>
        </div>

        <div className="gd-toggle-row gd-toggle-row-ingroup gd-toggle-row-border">
          <div className="gd-toggle-text">
            <span className="gd-toggle-label">AI Enhancement</span>
            <span className="gd-toggle-desc">
              Uses AI to improve readability and explanation quality on top of structured source facts.
            </span>
          </div>
          <button
            className={`gd-toggle-btn ${includeAI ? 'on' : ''}`}
            onClick={() => setIncludeAI(v => !v)}
            title={includeAI ? 'Disable AI enhancement' : 'Enable AI enhancement'}
          >
            {includeAI ? <ToggleRight size={28} /> : <ToggleLeft size={28} />}
          </button>
        </div>
      </div>
    </div>
  )

  // ── STEP 4: Review Plan ───────────────────────────────────────────────────

  const renderStep4 = () => {
    // Derive planned artifacts from selected doc types
    const ext = outputFormat === 'pdf' ? 'pdf' : outputFormat === 'html' ? 'html' : 'md'
    const plannedArtifacts = DOC_TYPES
      .filter(dt => isAvailable(dt) && selectedDocTypes.has(dt.id))
      .map(dt => ({
        filename: `${DOC_TYPE_SLUGS[dt.id] || dt.id}.${ext}`,
        label:    dt.label,
        icon:     dt.icon,
        desc:     dt.desc,
      }))

    // ── Running state ───────────────────────────────────────────────────
    if (genStatus === 'running') {
      return (
        <div className="gd-step-content gd-step-centered">
          <Loader2 size={40} className="gd-generate-icon spinning" />
          <h2 className="gd-step-title">Generating Documentation</h2>
          <p className="gd-step-subtitle">
            Processing <strong>{projectName}</strong> — please wait.
          </p>
          <div className="gd-progress-wrap">
            <div className="gd-progress-track">
              <div className="gd-progress-fill" style={{ width: `${genProgress}%` }} />
            </div>
            <div className="gd-progress-label">
              <span>{genMessage || 'Working…'}</span>
              <span className="gd-progress-pct">{genProgress}%</span>
            </div>
          </div>
          <div className="gd-gen-summary-running">
            <div className="gd-gen-row"><span>Types queued</span><span>{selectedDocTypes.size}</span></div>
            <div className="gd-gen-row"><span>Format</span><span>{outputFormat.toUpperCase()}</span></div>
            <div className="gd-gen-row"><span>AI Enhancement</span><span>{includeAI ? 'Enabled' : 'Disabled'}</span></div>
            {docJobId && (
              <div className="gd-gen-row">
                <span>Job ID</span>
                <span className="gd-job-id">{docJobId.slice(0, 8)}…</span>
              </div>
            )}
          </div>
        </div>
      )
    }

    // ── Failed state ────────────────────────────────────────────────────
    if (genStatus === 'failed') {
      return (
        <div className="gd-step-content gd-step-centered">
          <AlertCircle size={40} className="gd-error-icon" />
          <h2 className="gd-step-title">Generation Failed</h2>
          <p className="gd-step-subtitle">{genError || 'An unknown error occurred.'}</p>
          <div className="gd-retry-row">
            <button className="gd-generate-btn" onClick={() => {
              setGenStatus('idle')
              setGenError(null)
              setGenProgress(0)
              setDocJobId(null)
            }}>
              Try Again
            </button>
            {!caJobId && (
              <button className="gd-footer-btn secondary" onClick={onGoToAnalyze}>
                Go to Analyze
              </button>
            )}
          </div>
        </div>
      )
    }

    // ── Idle — Review Plan ──────────────────────────────────────────────
    return (
      <div className="gd-step-content">
        <div className="gd-step-header">
          <h2 className="gd-step-title">Review Plan</h2>
          <p className="gd-step-subtitle">
            Everything looks right? Click Generate Documentation to start.
          </p>
        </div>

        {/* Generation Plan card — header + two-column grid */}
        <div className="gd-plan-card">
          <div className="gd-plan-card-header">
            <span className="gd-plan-card-title">Generation Plan</span>
            <span className="gd-plan-card-project">{projectName}</span>
          </div>
          <div className="gd-plan-grid">
            <div className="gd-plan-cell">
              <span className="gd-plan-label">Scope</span>
              <span className="gd-plan-value">{SCOPE_OPTIONS.find(o => o.id === selectedScope)?.label}</span>
            </div>
            <div className="gd-plan-cell">
              <span className="gd-plan-label">Detail level</span>
              <span className="gd-plan-value">
                {detailLevel === 'detailed' ? 'Detailed' : 'Summary'}
              </span>
            </div>
            <div className="gd-plan-cell">
              <span className="gd-plan-label">Files</span>
              <span className="gd-plan-value">
                {selectedScope === 'all'
                  ? 'All analyzed files'
                  : selectedScope === 'programs'
                    ? 'Programs only'
                    : 'Selected files'}
              </span>
            </div>
            <div className="gd-plan-cell">
              <span className="gd-plan-label">Dependencies</span>
              <span className="gd-plan-value">{includeDeps ? 'Included' : 'Excluded'}</span>
            </div>
            <div className="gd-plan-cell">
              <span className="gd-plan-label">Package</span>
              <span className="gd-plan-value">
                {selectedDocTypes.size} artifact type{selectedDocTypes.size !== 1 ? 's' : ''}
              </span>
            </div>
            <div className="gd-plan-cell">
              <span className="gd-plan-label">AI enhancement</span>
              <span className="gd-plan-value">{includeAI ? 'Enabled' : 'Disabled'}</span>
            </div>
            <div className="gd-plan-cell">
              <span className="gd-plan-label">Output format</span>
              <span className="gd-plan-value">{outputFormat.toUpperCase()}</span>
            </div>
            <div className="gd-plan-cell">
              <span className="gd-plan-label">Destination</span>
              <span className="gd-plan-value">Project S3 artifacts</span>
            </div>
          </div>
        </div>

        {/* Artifacts queue */}
        <div className="gd-section">
          <h3 className="gd-section-label">
            Artifacts to be generated
            {plannedArtifacts.length > 0 && (
              <span className="gd-section-count">{plannedArtifacts.length}</span>
            )}
          </h3>
          <div className="gd-artifact-plan-list">
            {plannedArtifacts.length > 0
              ? plannedArtifacts.map((art, idx) => {
                  const Icon = art.icon
                  return (
                    <div key={art.filename} className="gd-artifact-plan-row">
                      <div className="gd-artifact-plan-seq">{idx + 1}</div>
                      <div className="gd-artifact-plan-iconbox"><Icon size={14} /></div>
                      <div className="gd-artifact-plan-meta">
                        <span className="gd-artifact-plan-name">{art.filename}</span>
                        <span className="gd-artifact-plan-label">{art.label}</span>
                      </div>
                      <span className="gd-artifact-plan-type">{outputFormat.toUpperCase()}</span>
                    </div>
                  )
                })
              : (
                <p className="gd-no-artifacts">
                  No artifact types selected — go back to Documentation Package.
                </p>
              )}
          </div>
        </div>

        {/* Generate action */}
        <div className="gd-plan-launch">
          <button
            className="gd-footer-btn secondary"
            onClick={() => setActiveStep(3)}
          >
            Back
          </button>
          <button
            className={`gd-launch-btn ${!caJobId || plannedArtifacts.length === 0 ? 'disabled' : ''}`}
            onClick={startGeneration}
            disabled={!caJobId || plannedArtifacts.length === 0}
            title={!caJobId ? 'No Code Analysis job linked — run Analyze first' : undefined}
          >
            <Play size={15} />
            Generate Documentation
          </button>
        </div>
      </div>
    )
  }

  // ── STEP 5: Results / Review & Export ────────────────────────────────────

  const renderStep5 = () => {
    const stepTitle = (
      outcomeStatus === 'partial'    ? 'Completed with Errors'
      : outcomeStatus === 'all-failed' ? 'Documentation Failed'
      : artifacts.length > 0         ? 'Documentation Package Ready'
      :                                 'Results'
    )

    return (
      <div className="gd-step-content">
        <div className="gd-step-header">
          <h2 className="gd-step-title">{stepTitle}</h2>
          <p className="gd-step-subtitle">
            {artifacts.length > 0
              ? outcomeStatus === 'partial'
                ? `${genSucceededCount} of ${genSucceededCount + genFailedCount} document(s) generated successfully.`
                : 'Your documentation package is ready. Review and export below.'
              : 'Run the generation plan to produce your documentation artifacts.'}
          </p>
        </div>

        {/* Results summary strip */}
        {artifacts.length > 0 && (
          <div className="gd-results-summary">
            <div className="gd-results-summary-item">
              <span className="gd-results-summary-label">Project</span>
              <span className="gd-results-summary-value">{projectName}</span>
            </div>
            <div className="gd-results-summary-item">
              <span className="gd-results-summary-label">Scope</span>
              <span className="gd-results-summary-value">
                {SCOPE_OPTIONS.find(o => o.id === selectedScope)?.label}
              </span>
            </div>
            <div className="gd-results-summary-item">
              <span className="gd-results-summary-label">Total Artifacts</span>
              <span className="gd-results-summary-value">{artifacts.length} generated</span>
            </div>
            {docJobId && (
              <div className="gd-results-summary-item">
                <span className="gd-results-summary-label">Job Reference</span>
                <span className="gd-results-summary-value gd-job-id">{docJobId.slice(0, 8)}…</span>
              </div>
            )}
          </div>
        )}

        {outcomeStatus === 'partial' && (
          <div className="gd-partial-notice">
            <AlertCircle size={15} />
            <span>
              Some documents failed due to incomplete analysis data.
              Re-running <strong>Code Analysis</strong> and generating again should resolve them.
            </span>
          </div>
        )}

        {/* Generated Artifacts */}
        <div className="gd-section">
          <h3 className="gd-section-label">Generated Artifacts</h3>
          {artifacts.length > 0 ? (
            <div className="gd-artifact-list">
              {artifacts.map(art => {
                const dt       = DOC_TYPES.find(d => d.id === art.doc_type)
                const Icon     = dt?.icon || FileText
                const isError  = art.format === 'error'
                const isExpanded = expandedErrors.has(art.doc_type)

                return (
                  <div key={art.doc_type} className={`gd-artifact-row ${isError ? 'error' : ''}`}>
                    <Icon size={16} className="gd-artifact-icon" />
                    <div className="gd-artifact-meta">
                      <span className="gd-artifact-name">{art.label || dt?.label || art.doc_type}</span>
                      {!isError && (
                        <span className="gd-artifact-stats">
                          {art.format?.toUpperCase()}
                          {art.line_count != null && ` · ${art.line_count.toLocaleString()} lines`}
                          {art.ai_enhanced && ' · AI enriched'}
                        </span>
                      )}
                      {isError && (
                        <>
                          <span className="gd-artifact-error-msg">
                            {getFriendlyErrorMessage(art)}
                          </span>
                          {art.error && (
                            <div className="gd-artifact-tech-wrap">
                              <button
                                className="gd-artifact-tech-toggle"
                                onClick={() => setExpandedErrors(prev => {
                                  const next = new Set(prev)
                                  if (next.has(art.doc_type)) next.delete(art.doc_type)
                                  else next.add(art.doc_type)
                                  return next
                                })}
                              >
                                <ChevronDown size={12} className={`gd-tech-chevron ${isExpanded ? 'open' : ''}`} />
                                Technical details
                              </button>
                              {isExpanded && (
                                <pre className="gd-artifact-tech-detail">{art.error}</pre>
                              )}
                            </div>
                          )}
                        </>
                      )}
                    </div>
                    {!isError && art.download_url && (
                      <>
                        <button
                          className="gd-artifact-download"
                          onClick={() => downloadArtifact(art.download_url, art.filename, art.format)}
                          title={`Download ${art.filename}`}
                        >
                          <Download size={14} />
                          Download
                        </button>
                        {art.format === 'markdown' && (
                          <button
                            className="gd-artifact-preview"
                            onClick={() => openPreview(art.download_url, art.label || dt?.label || art.doc_type, art.format)}
                            title="Preview in browser"
                          >
                            <Eye size={14} />
                            Preview
                          </button>
                        )}
                      </>
                    )}
                    {isError && <span className="gd-artifact-badge error">Failed</span>}
                  </div>
                )
              })}
            </div>
          ) : (
            <div className="gd-results-empty">
              <FileText size={28} className="gd-results-empty-icon" />
              <p>No artifacts yet — use Review Plan to start generation.</p>
            </div>
          )}
        </div>

        {/* File-Level Generation Status */}
        <div className="gd-section">
          <h3 className="gd-section-label">File-Level Generation Status</h3>
          <div className="gd-file-status-wrap">
            {artifacts.length > 0 ? (
              <table className="gd-file-status-table">
                <thead>
                  <tr>
                    <th>File</th>
                    <th>Type</th>
                    <th>Included In</th>
                    <th>Status</th>
                    <th>Notes</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td colSpan={5} className="gd-file-table-empty">
                      File-level status data will be available in a future update
                    </td>
                  </tr>
                </tbody>
              </table>
            ) : (
              <div className="gd-file-status-empty">
                File-level status will appear here after generation completes.
              </div>
            )}
          </div>
        </div>

        {/* Ask About These Docs — placeholder */}
        <div className="gd-ask-docs-area">
          <div className="gd-ask-docs-icon">
            <MessageSquare size={18} />
          </div>
          <div className="gd-ask-docs-text">
            <span className="gd-ask-docs-title">Ask About These Docs</span>
            <span className="gd-ask-docs-desc">
              Query and explore the generated documentation using AI. Coming soon.
            </span>
          </div>
          <span className="gd-ask-docs-badge">Coming soon</span>
        </div>

        {/* Bottom actions */}
        <div className="gd-results-actions">
          <button className="gd-footer-btn secondary" onClick={resetGeneration}>
            <RefreshCw size={14} />
            Generate Again
          </button>
          <button
            className="gd-footer-btn secondary"
            disabled={genSucceededCount === 0}
            title={genSucceededCount === 0 ? 'No artifacts to export' : 'Export all artifacts as a bundle'}
          >
            <Archive size={14} />
            Export Package
          </button>
          <button className="gd-footer-btn primary" onClick={onBack}>
            Done
          </button>
        </div>
      </div>
    )
  }

  // ── Step dispatch ─────────────────────────────────────────────────────────

  const renderStepContent = () => {
    switch (activeStep) {
      case 1: return renderStep1()
      case 2: return renderStep2()
      case 3: return renderStep3()
      case 4: return renderStep4()
      case 5: return renderStep5()
      default: return renderStep1()
    }
  }

  // ── Context panel ─────────────────────────────────────────────────────────

  const renderContextPanel = () => (
    <aside className="gd-context-panel">
      <p className="gd-context-heading">GENERATION SUMMARY</p>

      <div className="gd-context-block">
        <span className="gd-context-label">Project</span>
        <span className="gd-context-value">{projectName}</span>
      </div>

      <div className="gd-context-block">
        <span className="gd-context-label">Scope</span>
        <span className="gd-context-value">
          {SCOPE_OPTIONS.find(o => o.id === selectedScope)?.label || '—'}
        </span>
      </div>

      <div className="gd-context-block">
        <span className="gd-context-label">Doc Types Selected</span>
        <span className="gd-context-value">{selectedDocTypes.size} of {DOC_TYPES.length}</span>
      </div>

      <div className="gd-context-block">
        <span className="gd-context-label">Detail Level</span>
        <span className="gd-context-value">
          {detailLevel === 'detailed' ? 'Detailed' : 'Summary'}
        </span>
      </div>

      <div className="gd-context-block">
        <span className="gd-context-label">Output Format</span>
        <span className="gd-context-value">{outputFormat.toUpperCase()}</span>
      </div>

      <div className="gd-context-block">
        <span className="gd-context-label">AI Enhancement</span>
        <span className={`gd-context-value ${includeAI ? 'on' : 'off'}`}>
          {includeAI ? 'Enabled' : 'Disabled'}
        </span>
      </div>

      {caJobId && (
        <div className="gd-context-block">
          <span className="gd-context-label">CA Job</span>
          <span className="gd-context-value gd-job-id">{caJobId.slice(0, 8)}…</span>
        </div>
      )}

      {genStatus !== 'idle' && (
        <>
          <div className="gd-context-divider" />
          <div className="gd-context-block">
            <span className="gd-context-label">Status</span>
            <span className={`gd-context-value gd-status-${genStatus === 'completed' ? outcomeStatus : genStatus}`}>
              {genStatus === 'running'                                  ? `Running — ${genProgress}%`   : ''}
              {genStatus === 'completed' && outcomeStatus === 'completed'   ? 'Completed'               : ''}
              {genStatus === 'completed' && outcomeStatus === 'partial'     ? 'Partial Success'          : ''}
              {genStatus === 'completed' && outcomeStatus === 'all-failed'  ? 'Failed'                   : ''}
              {genStatus === 'failed'                                    ? 'Failed'                      : ''}
            </span>
          </div>
          {docJobId && (
            <div className="gd-context-block">
              <span className="gd-context-label">Doc Job</span>
              <span className="gd-context-value gd-job-id">{docJobId.slice(0, 8)}…</span>
            </div>
          )}
          {artifacts.length > 0 && (
            <div className="gd-context-block">
              <span className="gd-context-label">Artifacts</span>
              <span className="gd-context-value">
                {genSucceededCount} generated{genFailedCount > 0 ? `, ${genFailedCount} failed` : ''}
              </span>
            </div>
          )}
        </>
      )}

      <div className="gd-context-divider" />
      <p className="gd-context-note">
        Documentation is generated from the most recent Code Analysis run.
        Re-run Code Analysis to refresh source data.
      </p>
    </aside>
  )

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="gd-view">

      {/* ── Header ──────────────────────────────────────────────────────────── */}
      <div className="runs-header-bar">

        {/* Left: back + breadcrumb */}
        <div className="runs-header-left">
          <button className="runs-back-btn" onClick={onBack}>
            <ArrowLeft size={15} />
            <span>Runs</span>
          </button>
          <div className="runs-breadcrumb">
            <span className="runs-bc-project">{projectName}</span>
            <ChevronRight size={14} className="runs-bc-sep" />
            <span className="runs-bc-page">Generate Documentation</span>
          </div>
        </div>

        {/* Center: mode-switcher nav */}
        <div className="runs-header-center">
          <div className="mode-switcher">
            <button className="mode-btn" onClick={onBackToProjects || onBack} title="Projects">
              <Home size={16} />
              <span>Home</span>
            </button>
            <button
              className="mode-btn"
              onClick={() => onGoToAnalyze?.()}
              title="Analyze — review assets and dependencies"
            >
              <BarChart2 size={16} />
              <span>Analyze</span>
            </button>
            {/* Workflow and MCP hidden — accessible via Journey dropdown
            <button className="mode-btn" disabled title="Workflow — not available from this screen">
              <Workflow size={16} />
              <span>Workflow</span>
            </button>
            <button className="mode-btn" disabled title="MCP — coming soon">
              <Plug size={16} />
              <span>MCP</span>
            </button>
            */}
            <button
              className="mode-btn"
              onClick={() => onOpenReports?.()}
              title="Reports"
            >
              <FileBarChart size={16} />
              <span>Reports</span>
            </button>

            {/* More menu */}
            <div className="more-menu-container">
              <button
                className={`mode-btn more-trigger ${showMoreMenu ? 'active' : ''}`}
                onClick={() => setShowMoreMenu(v => !v)}
                disabled={false}
                title="HITL Journey"
              >
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="4" y1="6" x2="20" y2="6" />
                  <line x1="4" y1="12" x2="20" y2="12" />
                  <line x1="4" y1="18" x2="20" y2="18" />
                </svg>
                <ChevronDown size={12} className={`more-chevron ${showMoreMenu ? 'open' : ''}`} />
              </button>

              {showMoreMenu && (
                <>
                  <div className="more-backdrop" onClick={() => setShowMoreMenu(false)} />
                  <div className="more-dropdown">
                    <div className="dropdown-section-label">MODERNIZATION FLOW</div>
                    <button className="dropdown-item" onClick={() => { setShowMoreMenu(false); onGoToAnalyze?.() }}>
                      <span>Analyze</span>
                    </button>
                    <button className="dropdown-item active-item" onClick={() => setShowMoreMenu(false)}>
                      <span>Generate Documentation</span>
                    </button>
                    {/* EBL disabled — graph + AIL replaces it (see docs/20260721_define_services_architecture.md)
                    <button className="dropdown-item" onClick={() => { setShowMoreMenu(false); onGoToExtractBizLogic?.(caJobId) }}>
                      <span>Extract Business Logic</span>
                    </button>
                    */}
                    <div className="dropdown-divider" />
                    <button className="dropdown-item" onClick={() => { setShowMoreMenu(false); onGoToDecomposeCode?.() }}>
                      <span>Define Services</span>
                    </button>
                    <button className="dropdown-item" onClick={() => { setShowMoreMenu(false); onGoToServiceGeneration?.() }}>
                      <span>Build Services</span>
                    </button>
                    <div className="dropdown-divider" />
                    <button className="dropdown-item" onClick={() => { setShowMoreMenu(false); onGoToRefactorCode?.() }}>
                      <span>Refactor Code</span>
                    </button>
                    <button className="dropdown-item" onClick={() => { setShowMoreMenu(false); onGoToRefineCode?.() }}>
                      <span>Refine Code</span>
                    </button>
                    <div className="dropdown-divider" />
                    <button className="dropdown-item" onClick={() => { setShowMoreMenu(false); onGoToJavaPkg?.([]) }}>
                      <span>Build / Package</span>
                    </button>
                    <div className="dropdown-divider" />
                    <button className="dropdown-item" onClick={() => { setShowMoreMenu(false); onOpenReports?.() }}>
                      <span>Reports</span>
                    </button>
                    <button className="dropdown-item" onClick={() => { setShowMoreMenu(false); onOpenSettings?.() }}>
                      <span>Settings</span>
                    </button>
                  </div>
                </>
              )}
            </div>

          </div>
        </div>

        {/* Right: settings, help, user menu */}
        <div className="runs-header-right">
          <button className="icon-btn" onClick={onOpenSettings} title="Settings">
            <Settings size={18} />
          </button>
          <button className="icon-btn" onClick={onOpenHelp} title="Help">
            <HelpCircle size={18} />
          </button>
          <div className="user-menu-container">
            <button className="user-btn" onClick={() => setShowUserMenu(v => !v)}>
              <div className="user-avatar"><User size={16} /></div>
              <span>{getShortName()}</span>
              <ChevronDown size={14} />
            </button>
            {showUserMenu && (
              <div className="user-dropdown">
                <div className="dropdown-header">
                  <span className="user-name">{userProfile.displayName || 'User'}</span>
                  <span className="user-email">{userProfile.email || ''}</span>
                </div>
                <div className="dropdown-divider" />
                <button className="dropdown-item" onClick={() => { setShowUserMenu(false); onOpenSettings?.() }}>
                  <Settings size={16} />
                  <span>Settings</span>
                </button>
                <button className="dropdown-item" onClick={() => { setShowUserMenu(false); onBack() }}>
                  <Home size={16} />
                  <span>Back to Runs</span>
                </button>
              </div>
            )}
          </div>
        </div>

      </div>

      {/* ── Body: sidebar + main content + context panel ─────────────────── */}
      <div className="gd-body">

        {/* Left sidebar — step guide */}
        <aside className="gd-sidebar">
          <p className="gd-sidebar-heading">DOCUMENTATION WORKFLOW</p>
          <p className="gd-sidebar-sub">Follow the steps to generate your artifacts</p>
          <div className="gd-step-list">
            {STEPS.map(renderStepItem)}
          </div>
        </aside>

        {/* Main content — step-specific */}
        <main className="gd-main">
          {renderStepContent()}

          {/* Step navigation footer — steps 1–3 only */}
          {showFooter && (
            <div className="gd-step-footer">
              {activeStep > 1 && (
                <button className="gd-footer-btn secondary" onClick={() => setActiveStep(s => s - 1)}>
                  Back
                </button>
              )}
              <button
                className="gd-footer-btn primary"
                onClick={() => setActiveStep(s => s + 1)}
                disabled={!canAdvance()}
              >
                Continue
                <ChevronRight size={15} />
              </button>
            </div>
          )}
        </main>

        {/* Right context panel */}
        {renderContextPanel()}

      </div>

      {/* ── Preview Modal ─────────────────────────────────────────────────── */}
      {previewOpen && (
        <div className="gd-preview-backdrop" onClick={closePreview}>
          <div className="gd-preview-modal" onClick={e => e.stopPropagation()}>
            <div className="gd-preview-header">
              <span className="gd-preview-title">{previewLabel}</span>
              <button className="gd-preview-close" onClick={closePreview}>
                <X size={16} />
              </button>
            </div>
            <div className="gd-preview-body">
              {previewLoading
                ? <div className="gd-preview-loading"><Loader2 size={24} className="spinning" /></div>
                : previewFormat === 'pdf'
                  ? <iframe src={previewText} className="gd-preview-iframe" title={previewLabel} />
                  : previewFormat === 'html'
                    ? <div className="gd-preview-html" dangerouslySetInnerHTML={{ __html: previewText }} />
                    : <pre className="gd-preview-pre">{previewText}</pre>}
            </div>
          </div>
        </div>
      )}

    </div>
  )
}

export default GenerateDocsView
